import { useId, type ReactNode } from "react";
import { Checkbox as C, Collapsible } from "radix-ui";
import { Check, ChevronDown, Lock, type LucideIcon } from "lucide-react";
import { cn } from "../../lib/cn";
import { fill } from "../../lib/fill";
import { useKitCopy } from "../../lib/kit-copy";
import type { KitLabels } from "../../lib/kit-labels";
import { Chip, severityIcon, severityLabelKey, type Confidence, type Severity, ConfidenceBadge } from "../primitives/chip";
import { Button } from "../primitives/button";
import { Sparkline } from "../charts/charts";

export const sevStroke: Record<Severity, string> = {
  exploited: "var(--color-exploited)",
  high: "var(--color-high)",
  medium: "var(--color-medium)",
  low: "var(--color-low)",
  ok: "var(--color-ok)",
};
const sevTextClass: Record<Severity, string> = { exploited: "text-exploited", high: "text-high", medium: "text-medium", low: "text-low", ok: "text-ok" };

/** Status ring (05 §4): colour + icon inside; pair with a text label. */
export function StatusRing({ severity, size = 40 }: { severity: Severity; size?: number }) {
  const Icon = severityIcon[severity];
  const r = size / 2 - 3;
  const c = 2 * Math.PI * r;
  const frac = severity === "ok" ? 1 : severity === "exploited" ? 0.25 : 0.6;
  return (
    <span aria-hidden className="relative inline-grid shrink-0 place-items-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--color-border)" strokeWidth="3" />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={sevStroke[severity]} strokeWidth="3" strokeDasharray={`${c * frac} ${c}`} strokeLinecap="round" />
      </svg>
      <Icon className={cn("absolute", sevTextClass[severity])} style={{ width: size * 0.42, height: size * 0.42 }} strokeWidth={1.75} />
    </span>
  );
}

/** `sparkLabel`: the sparkline's accessible name, `{name}` filled in; default the `findingsSpark` label (key ui.findings_over_the_last_8_weeks). */
export function ProductCard({ name, kind, version, severity, statusText, meta, spark, sparkLabel }: { name: string; kind: string; version: string; severity: Severity; statusText: string; meta: string; spark?: number[]; sparkLabel?: string }) {
  const l = useKitCopy().labels;
  return (
    <article className="card p-4" aria-label={`${name}: ${statusText}`}>
      <div className="flex items-start gap-3">
        <StatusRing severity={severity} />
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold">{name}</h3>
          <p className="text-2xs text-text-muted">{kind}</p>
        </div>
        <Chip className="font-mono" icon={null}>
          v{version}
        </Chip>
      </div>
      <div className="mt-4 flex items-end justify-between gap-3">
        <div className="space-y-0.5 text-2xs text-text-muted">
          <p className={cn("font-semibold", sevTextClass[severity])}>{statusText}</p>
          <p>{meta}</p>
        </div>
        {spark && <Sparkline data={spark} severity={severity} label={fill(sparkLabel ?? l.findingsSpark, { name })} />}
      </div>
    </article>
  );
}

export function KpiTile({ label, value, note, icon: Icon }: { label: string; value: ReactNode; note?: ReactNode; icon?: LucideIcon }) {
  return (
    <div className="card flex items-start gap-3 p-4">
      {Icon && (
        <span aria-hidden className="grid size-10 shrink-0 place-items-center rounded-full bg-brand-50 text-brand-600">
          <Icon className="size-5" strokeWidth={1.75} />
        </span>
      )}
      <div>
        <p className="micro-label">{label}</p>
        <p className="heading tnum mt-1 text-2xl">{value}</p>
        {note && <p className="text-2xs text-text-muted">{note}</p>}
      </div>
    </div>
  );
}

/** "Needs attention" item: what it is, what it means, one action. */
export function AttentionItem({ severity, title, detail, aside, action }: { severity: Severity; title: string; detail: string; aside?: ReactNode; action: ReactNode }) {
  const Icon = severityIcon[severity];
  return (
    <li className="flex flex-col gap-3 rounded-card bg-surface p-4 shadow-card sm:flex-row sm:items-center">
      <span aria-hidden className={cn("grid size-9 shrink-0 place-items-center rounded-full bg-surface-2", sevTextClass[severity])}>
        <Icon className="size-4.5" strokeWidth={1.75} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold">{title}</p>
        <p className="text-xs text-text-muted">{detail}</p>
      </div>
      {aside}
      {action}
    </li>
  );
}

export function PlanCard({ name, price, period, features, recommended, current, onChoose }: { name: string; price: string; period: string; features: string[]; recommended?: boolean; current?: boolean; onChoose: () => void }) {
  const l = useKitCopy().labels;
  return (
    <article aria-label={fill(l.namePlan, { name })} className={cn("card flex flex-col p-5", recommended && "ring-2 ring-brand-600")}>
      <div className="flex items-center justify-between gap-2">
        <h3 className="heading text-lg">{name}</h3>
        {recommended && <Chip tone="brand">{l.recommended}</Chip>}
      </div>
      <p className="mt-3">
        <span className="heading tnum text-3xl">{price}</span> <span className="text-sm text-text-muted">{period}</span>
      </p>
      <ul className="mt-4 flex-1 space-y-2 text-sm">
        {features.map((f) => (
          <li key={f} className="flex gap-2">
            <Check aria-hidden className="mt-0.5 size-4 shrink-0 text-ok" /> {f}
          </li>
        ))}
      </ul>
      <Button variant={recommended ? "primary" : "secondary"} className="mt-5 w-full" disabled={current} onClick={onChoose}>
        {current ? l.yourPlan : fill(l.chooseName, { name })}
      </Button>
    </article>
  );
}

/**
 * Source card (onboarding, 04 §1.5): selectable (checkbox semantics) with an
 * expanded config state for the selected source.
 */
export function SourceCard({
  icon: Icon,
  title,
  description,
  selected,
  onSelectedChange,
  children,
  badge,
  locked,
}: {
  icon: LucideIcon;
  title: string;
  description: string;
  selected: boolean;
  onSelectedChange: (v: boolean) => void;
  children?: ReactNode;
  /** e.g. "Recommended" */
  badge?: string;
  /** Plan lock or "coming later": the card can't be selected and says why. */
  locked?: string;
}) {
  const id = useId();
  const l = useKitCopy().labels;
  return (
    <Collapsible.Root
      open={selected && !locked && !!children}
      className={cn(
        "rounded-card border bg-surface transition-colors duration-(--duration-hover)",
        selected && !locked ? "border-brand-600 ring-1 ring-brand-600/30" : "border-border",
        !locked && !selected && "hover:border-border-input",
        locked && "bg-surface-2",
      )}
    >
      <label htmlFor={id} className={cn("flex min-h-touch items-start gap-3 p-4", locked ? "cursor-not-allowed" : "cursor-pointer")}>
        <span aria-hidden className={cn("grid size-10 shrink-0 place-items-center rounded-full", selected ? "bg-brand-600 text-on-brand" : "bg-surface-2 text-text-muted")}>
          <Icon className="size-5" strokeWidth={1.75} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-2 text-sm font-semibold">
            {title}
            {badge && <Chip tone="brand" icon={null}>{badge}</Chip>}
          </span>
          <span className="block text-xs text-text-muted">{description}</span>
          {locked && (
            <span className="mt-1 flex items-center gap-1 text-2xs font-medium text-text-muted">
              <Lock aria-hidden className="size-3" /> {locked}
            </span>
          )}
        </span>
        <C.Root id={id} checked={selected && !locked} disabled={!!locked} onCheckedChange={(v) => onSelectedChange(v === true)} className="mt-0.5 grid size-5 shrink-0 place-items-center rounded-xs border border-border-input bg-surface data-[state=checked]:border-brand-600 data-[state=checked]:bg-brand-600">
          <C.Indicator className="text-on-brand">
            <Check aria-hidden className="size-3.5" strokeWidth={3} />
          </C.Indicator>
        </C.Root>
      </label>
      {children && (
        <Collapsible.Content className="border-t border-border p-4">
          <div className="flex items-center gap-1 pb-2 text-2xs font-semibold text-text-muted">
            <ChevronDown aria-hidden className="size-3.5" /> {fill(l.setUpTitle, { title })}
          </div>
          {children}
        </Collapsible.Content>
      )}
    </Collapsible.Root>
  );
}

/** API §13 component occurrence `method`, with the words shown to people. */
export type DetectionMethod = "package" | "fingerprint" | "imported" | "manual";
export const detectionMethodLabel: Record<DetectionMethod, string> = { package: "package metadata", fingerprint: "binary fingerprint", imported: "SBOM import", manual: "added by hand" };
/** Each detection method's words as a `KitLabels` name (the row takes them from `KitCopyProvider`). */
export const detectionMethodLabelKey: Record<DetectionMethod, keyof KitLabels> = { package: "methodPackage", fingerprint: "methodFingerprint", imported: "methodImported", manual: "methodManual" };

/** Component row (05 §4): name, version, licence, where it was found, detection method and confidence. */
export function ComponentRow({ name, version, purl, licence, foundIn, method, confidence, finding, select }: { name: string; version: string; purl?: string; licence: string; foundIn: string; method: DetectionMethod; confidence: Confidence; finding?: Severity; select?: ReactNode }) {
  const l = useKitCopy().labels;
  return (
    <tr className="border-b border-border last:border-b-0 hover:bg-surface-2/60">
      {select && <td className="w-10 pl-4">{select}</td>}
      <th scope="row" className="py-2.5 pl-4 pr-3 text-left font-normal">
        <span className="block text-sm font-medium">{name}</span>
        {purl && <span className="block truncate font-mono text-2xs text-text-muted">{purl}</span>}
      </th>
      <td className="px-3 py-2.5 font-mono text-xs">{version}</td>
      <td className="px-3 py-2.5 text-xs">{licence}</td>
      <td className="px-3 py-2.5 text-xs text-text-muted">{foundIn}</td>
      <td className="px-3 py-2.5">
        <Chip tone="neutral" icon={null}>
          {l[detectionMethodLabelKey[method]]}
        </Chip>
      </td>
      <td className="px-3 py-2.5">
        <ConfidenceBadge level={confidence} />
      </td>
      <td className="py-2.5 pl-3 pr-4">{finding ? <Chip tone={finding}>{finding === "ok" ? l.noKnownIssues : l[severityLabelKey[finding]]}</Chip> : <span className="text-2xs text-text-muted">{l.noKnownIssues}</span>}</td>
    </tr>
  );
}
